/**
 * RenotifyDialog
 * Lets the user set a renotification for a parked alert either:
 *   1. Price-based  — fire when price crosses a level (above / below)
 *   2. Time-based   — fire after N minutes / hours / days
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, ToggleButtonGroup, ToggleButton, TextField,
  Select, MenuItem, FormControl, InputLabel, Box, Typography,
  InputAdornment
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useAppTheme } from '../../hooks/useAppTheme';

type Mode = 'price' | 'time';

interface Props {
  open: boolean;
  symbol: string;
  currentPrice?: number;
  decimalPrecision?: number;
  existingRenotifyAt?: number;
  existingRenotifyAfterTime?: string;
  defaultDirection?: 'above' | 'below';
  title?: string;
  onClose: () => void;
  onSave: (payload: {
    renotifyAt?: number;
    renotifyDirection?: 'above' | 'below';
    renotifyAfterTime?: string;  // ISO timestamp
  }) => void;
}

const RenotifyDialog: React.FC<Props> = ({
  open, symbol, currentPrice, decimalPrecision, existingRenotifyAt, existingRenotifyAfterTime,
  defaultDirection, title, onClose, onSave
}) => {
  const { C } = useAppTheme();

  const [mode, setMode] = useState<Mode>('price');
  const [priceStr, setPriceStr] = useState('');
  const [direction, setDirection] = useState<'above' | 'below'>('above');
  const [timeAmount, setTimeAmount] = useState('30');
  const [timeUnit, setTimeUnit] = useState<'minutes' | 'hours' | 'days'>('minutes');

  useEffect(() => {
    if (!open) return;
    // Pre-fill from existing values
    if (existingRenotifyAt) {
      setMode('price');
      setPriceStr(String(existingRenotifyAt));
      setDirection(currentPrice != null && currentPrice > existingRenotifyAt ? 'below' : 'above');
    } else if (existingRenotifyAfterTime) {
      setMode('time');
    } else {
      // Defaults
      setPriceStr(currentPrice ? String(currentPrice) : '');
      setDirection(defaultDirection || 'above');
      setTimeAmount('30');
      setTimeUnit('minutes');
    }
  }, [open, existingRenotifyAt, existingRenotifyAfterTime, currentPrice, defaultDirection]);

  const handleSave = () => {
    if (mode === 'price') {
      const val = parseFloat(priceStr);
      if (isNaN(val) || val <= 0) return;
      onSave({ renotifyAt: val, renotifyDirection: direction });
    } else {
      const amount = parseInt(timeAmount, 10);
      if (isNaN(amount) || amount <= 0) return;
      const msMap = { minutes: 60_000, hours: 3_600_000, days: 86_400_000 };
      const targetTime = new Date(Date.now() + amount * msMap[timeUnit]);
      onSave({ renotifyAfterTime: targetTime.toISOString() });
    }
  };

  const priceValid = mode !== 'price' || (parseFloat(priceStr) > 0);
  const timeValid = mode !== 'time' || (parseInt(timeAmount, 10) > 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <NotificationsIcon sx={{ color: C.accent, fontSize: 20 }} />
        {title || 'ReNotify'} — {symbol}
      </DialogTitle>
      <DialogContent>
        {currentPrice && (
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1.5 }}>
            Current price: <strong>{currentPrice.toLocaleString('en-IN', { minimumFractionDigits: decimalPrecision ?? 2, maximumFractionDigits: decimalPrecision ?? 2 })}</strong>
          </Typography>
        )}

        <ToggleButtonGroup
          size="small" exclusive
          value={mode}
          onChange={(_, v) => { if (v) setMode(v); }}
          fullWidth
          sx={{ mb: 2 }}
        >
          <ToggleButton value="price" sx={{ gap: 0.5 }}>
            <NotificationsIcon sx={{ fontSize: 14 }} /> At Price
          </ToggleButton>
          <ToggleButton value="time" sx={{ gap: 0.5 }}>
            <AccessTimeIcon sx={{ fontSize: 14 }} /> After Time
          </ToggleButton>
        </ToggleButtonGroup>

        {mode === 'price' ? (
          <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
            <TextField
              label="Renotify Price"
              size="small"
              type="number"
              value={priceStr}
              onChange={e => setPriceStr(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start">₹</InputAdornment>
              }}
              fullWidth
            />
            <ToggleButtonGroup
              size="small" exclusive
              value={direction}
              onChange={(_, v) => { if (v) setDirection(v); }}
              fullWidth
            >
              <ToggleButton value="above" sx={{ color: C.bull }}>
                Price rises to ↑
              </ToggleButton>
              <ToggleButton value="below" sx={{ color: C.bear }}>
                Price falls to ↓
              </ToggleButton>
            </ToggleButtonGroup>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              You'll be notified when {symbol} price goes {direction} {priceStr || '…'}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              label="Amount"
              size="small"
              type="number"
              value={timeAmount}
              onChange={e => setTimeAmount(e.target.value)}
              inputProps={{ min: 1, max: 9999 }}
              sx={{ width: 120 }}
            />
            <FormControl size="small" sx={{ minWidth: 110 }}>
              <InputLabel>Unit</InputLabel>
              <Select
                value={timeUnit}
                label="Unit"
                onChange={e => setTimeUnit(e.target.value as any)}
              >
                <MenuItem value="minutes">Minutes</MenuItem>
                <MenuItem value="hours">Hours</MenuItem>
                <MenuItem value="days">Days</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="caption" sx={{ color: 'text.secondary', flex: 1 }}>
              Notify in {timeAmount} {timeUnit}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} size="small">Cancel</Button>
        <Button
          variant="contained"
          size="small"
          onClick={handleSave}
          disabled={!priceValid || !timeValid}
          sx={{ bgcolor: C.accent }}
        >
          Set ReNotify
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RenotifyDialog;
